/**
 * Collection Template JavaScript
 * Handles read more/less functionality for collection descriptions
 */
class CollectionTemplate {
  constructor() {
    this.content = null;
    this.toggleBtn = null;
    this.isExpanded = false;
    this.fullHTML = '';
    this.truncatedHTML = '';
    this.maxChars = 600;
    this.resizeTimeout = null;
    
    this.init();
  }

  /**
   * Initialize the collection template functionality
   */
  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.content = document.querySelector('.description-content');
      this.toggleBtn = document.querySelector('.read-more-toggle');
      
      this.initReadMore();
      this.bindEvents();
    });
  }

  /**
   * Initialize read more functionality
   */
  initReadMore() {
    if (!this.content || !this.toggleBtn) return;

    const isMobile = this.isMobileDevice();

    if (isMobile) {
      this.handleMobileView();
    } else {
      this.handleDesktopView();
    }
  }

  /**
   * Check if current device is mobile
   * @returns {boolean}
   */
  isMobileDevice() {
    return window.innerWidth <= 768;
  }

  /**
   * Handle mobile view functionality
   */
  handleMobileView() {
    this.fullHTML = this.content.innerHTML;
    const fullText = this.content.textContent || this.content.innerText || '';

    if (fullText.length > this.maxChars) {
      this.truncatedHTML = this.createTruncatedHTML();
      this.setTruncatedState();
      this.bindToggleEvent();
    } else {
      this.hideToggleButton();
    }
  }

  /**
   * Handle desktop view functionality
   */
  handleDesktopView() {
    this.hideToggleButton();
  }

  /**
   * Create truncated HTML while preserving formatting
   * @returns {string}
   */
  createTruncatedHTML() {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.fullHTML;

    let charCount = 0;
    let found = false;

    const truncateNode = (node) => {
      if (found) return;

      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = node.textContent.length;

        if (charCount + textLength > this.maxChars) {
          const remaining = this.maxChars - charCount;
          const text = node.textContent;
          const lastSpace = text.lastIndexOf(' ', remaining);
          const cutPoint = lastSpace > remaining - 50 ? lastSpace : remaining;

          node.textContent = text.substring(0, cutPoint) + '...';
          found = true;
          charCount = this.maxChars;
        } else {
          charCount += textLength;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const children = Array.from(node.childNodes);
        for (let i = 0; i < children.length; i++) {
          truncateNode(children[i]);
          if (found) {
            // Remove all following siblings
            for (let j = i + 1; j < children.length; j++) {
              children[j].remove();
            }
            break;
          }
        }
      }
    };

    truncateNode(tempDiv);
    return tempDiv.innerHTML;
  }

  /**
   * Set the initial truncated state
   */
  setTruncatedState() {
    this.content.innerHTML = this.truncatedHTML;
    this.toggleBtn.style.display = 'inline-block';
    this.toggleBtn.textContent = 'Read more';
    this.isExpanded = false;
  }

  /**
   * Hide the toggle button
   */
  hideToggleButton() {
    if (this.toggleBtn) {
      this.toggleBtn.style.display = 'none';
    }
  }

  /**
   * Bind toggle button click event
   */
  bindToggleEvent() {
    if (this.toggleBtn) {
      this.toggleBtn.addEventListener('click', (e) => this.handleToggleClick(e));
    }
  }

  /**
   * Handle toggle button click
   * @param {Event} e - Click event
   */
  handleToggleClick(e) {
    e.preventDefault();
    this.isExpanded = !this.isExpanded;
    this.content.innerHTML = this.isExpanded ? this.fullHTML : this.truncatedHTML;
    this.toggleBtn.textContent = this.isExpanded ? 'Read less' : 'Read more';
  }

  /**
   * Bind all necessary events
   */
  bindEvents() {
    // Handle window resize with debouncing
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.initReadMore(), 250);
    });
  }

  /**
   * Destroy the instance and clean up event listeners
   */
  destroy() {
    if (this.toggleBtn) {
      this.toggleBtn.removeEventListener('click', this.handleToggleClick);
    }
    window.removeEventListener('resize', this.initReadMore);
    clearTimeout(this.resizeTimeout);
  }
}

// Initialize the collection template when the script loads
new CollectionTemplate();
